import { http } from "@/shared/lib/api/http"
import { BranchType } from "@ibada/shared-types/common"
import {
  FinanceTransferStatus,
  type ApproveFinanceTransferRequestInput,
  type CreateFinanceTransferRequestInput,
  type FinanceTransferNamedRef,
  type FinanceTransferRequestListQuery,
  type FinanceTransactionUserSummary,
  type FinanceTransferRequestRecord,
  type FinanceTransferDestination,
  TransferDerivationMode,
  type DeriveTransferAmountQuery,
  type TransferDerivationInput,
  type TransferDerivationResult,
  type CreateSavedTransferCalculationInput,
  type SavedTransferCalculation,
} from "@/features/finance/model/finance-types"
import {
  extractPaginatedListPayload,
  normalizeMoney,
  normalizeNullableString,
  normalizeNumber,
  normalizeString,
  ZERO_MONEY,
} from "./finance-api-shared"

/**
 * Cross-context transfer requests (IBA-158) — branch <-> parish, branch <-> branch.
 *
 * A separate surface from `/finance/transfers`, which stays the same-branch instant path. The two are
 * different shapes: one has a receiving side that must accept before anything posts, the other does not.
 */

function normalizeNamed(value: unknown): FinanceTransferNamedRef {
  if (!value || typeof value !== "object") return null
  const record = value as Record<string, unknown>
  const id = normalizeString(record.id)
  const name = normalizeString(record.name)
  const nameTranslationKey = normalizeNullableString(record.nameTranslationKey ?? record.name_translation_key)
  return id && name ? { id, name, nameTranslationKey } : null
}

function normalizeBranchRef(value: unknown): FinanceTransferRequestRecord["sourceBranch"] {
  if (!value || typeof value !== "object") return null
  const record = value as Record<string, unknown>
  const id = normalizeString(record.id)
  const name = normalizeString(record.name)
  const slug = normalizeString(record.slug)
  if (!id || !name) return null
  return {
    id,
    name,
    slug: slug ?? "",
    // NORMAL is the safe default: mistaking the parish for a branch shows a plain name, while mistaking a
    // branch for the parish would send PARISH-scoped lookups the API rejects.
    branchType: record.branchType === BranchType.System ? BranchType.System : BranchType.Normal,
  }
}

function normalizeStatus(value: unknown): FinanceTransferStatus {
  const status = normalizeString(value)
  const known = Object.values(FinanceTransferStatus) as string[]
  // An unknown status is treated as PENDING rather than crashing the list: a transfer whose state we cannot
  // read is exactly the one an operator most needs to see, and it renders read-only either way.
  return known.includes(status ?? "") ? (status as FinanceTransferStatus) : FinanceTransferStatus.Pending
}

function normalizeUser(value: unknown): FinanceTransactionUserSummary | null {
  if (!value || typeof value !== "object") return null
  const record = value as Record<string, unknown>
  const id = normalizeString(record.id)
  if (!id) return null
  return {
    id,
    email: normalizeString(record.email) ?? "",
    firstName: normalizeString(record.firstName) ?? "",
    lastName: normalizeString(record.lastName) ?? "",
    avatarUrl: normalizeNullableString(record.avatarUrl),
  }
}

function normalizeTransferRequest(input: unknown): FinanceTransferRequestRecord {
  const record = (input ?? {}) as Record<string, unknown>
  const currency = normalizeString(record.currency) ?? ""
  return {
    id: normalizeString(record.id) ?? "",
    workspaceId: normalizeString(record.workspaceId) ?? "",
    status: normalizeStatus(record.status),
    transferReference: normalizeNullableString(record.transferReference),
    // Money arrives as a canonical decimal string and stays one all the way to the renderer (IBA-116).
    sourceAmount: normalizeMoney(record.sourceAmount) ?? "0",
    destinationAmount: normalizeMoney(record.destinationAmount) ?? "0",
    currency,
    sourceBranchId: normalizeString(record.sourceBranchId) ?? "",
    destinationBranchId: normalizeString(record.destinationBranchId) ?? "",
    sourceBranch: normalizeBranchRef(record.sourceBranch),
    destinationBranch: normalizeBranchRef(record.destinationBranch),
    sourceAccountId: normalizeString(record.sourceAccountId) ?? "",
    sourceAccount: normalizeNamed(record.sourceAccount),
    destinationAccountId: normalizeNullableString(record.destinationAccountId),
    destinationAccount: normalizeNamed(record.destinationAccount),
    sourceFundId: normalizeString(record.sourceFundId) ?? "",
    sourceFund: normalizeNamed(record.sourceFund),
    destinationFundId: normalizeNullableString(record.destinationFundId),
    destinationFund: normalizeNamed(record.destinationFund),
    reference: normalizeNullableString(record.reference),
    description: normalizeNullableString(record.description),
    occurredAt: normalizeString(record.occurredAt) ?? "",
    submittedAt: normalizeNullableString(record.submittedAt),
    approvedAt: normalizeNullableString(record.approvedAt),
    rejectedAt: normalizeNullableString(record.rejectedAt),
    rejectionReason: normalizeNullableString(record.rejectionReason),
    cancelledAt: normalizeNullableString(record.cancelledAt),
    cancellationReason: normalizeNullableString(record.cancellationReason),
    sourceTransactionId: normalizeNullableString(record.sourceTransactionId),
    reversedAt: normalizeNullableString(record.reversedAt),
    reversalReason: normalizeNullableString(record.reversalReason),
    destinationTransactionId: normalizeNullableString(record.destinationTransactionId),
    createdByUser: normalizeUser(record.createdByUser),
    submittedByUser: normalizeUser(record.submittedByUser),
    approvedByUser: normalizeUser(record.approvedByUser),
    rejectedByUser: normalizeUser(record.rejectedByUser),
    cancelledByUser: normalizeUser(record.cancelledByUser),
    createdAt: normalizeString(record.createdAt) ?? "",
    updatedAt: normalizeString(record.updatedAt) ?? "",
  }
}

export async function listFinanceTransferRequests(query: FinanceTransferRequestListQuery): Promise<{
  items: FinanceTransferRequestRecord[]
  total: number
  page: number
  limit: number
}> {
  const params: Record<string, string | number> = { workspaceId: query.workspaceId }
  if (query.sourceBranchId) params.sourceBranchId = query.sourceBranchId
  if (query.destinationBranchId) params.destinationBranchId = query.destinationBranchId
  // Either side. The params are built explicitly, so a filter the client does not forward is a filter the
  // API never sees — which is how the All tab first rendered an empty table for a branch with four transfers.
  if (query.branchId) params.branchId = query.branchId
  if (query.status) params.status = query.status
  params.page = query.page ?? 1
  params.limit = query.limit ?? 20

  const response = await http.get<unknown>("/finance/transfers/requests", { params })
  return extractPaginatedListPayload<FinanceTransferRequestRecord>(
    response.data,
    ["items", "transfers", "data"],
    normalizeTransferRequest,
  )
}

/**
 * The contexts the active branch may SEND to — every active branch in the workspace except itself, parish
 * included.
 *
 * NOT `accessibleBranches`, and not `GET /branches`. The first lists branches the operator can administer,
 * which left a branch treasurer with an empty destination list and made branch -> parish impossible to start.
 * The second needs the Branches (workspace governance) permission, which a finance-only user does not hold.
 * This endpoint is gated on finance Write over the SOURCE branch — the same authority creating a transfer
 * already requires.
 */
export async function listFinanceTransferDestinations(query: {
  workspaceId: string
  sourceBranchId: string
}): Promise<FinanceTransferDestination[]> {
  const response = await http.get<unknown>("/finance/transfers/requests/destinations", {
    params: { workspaceId: query.workspaceId, sourceBranchId: query.sourceBranchId },
  })
  const payload = response.data
  const items = Array.isArray(payload)
    ? payload
    : Array.isArray((payload as { items?: unknown })?.items)
      ? ((payload as { items: unknown[] }).items)
      : []
  return items
    .map((item) => {
      const record = (item ?? {}) as Record<string, unknown>
      const id = normalizeString(record.id)
      const name = normalizeString(record.name)
      if (!id || !name) return null
      return {
        id,
        name,
        slug: normalizeString(record.slug) ?? "",
        branchType: normalizeString(record.branchType) ?? "",
      }
    })
    .filter((item): item is FinanceTransferDestination => item !== null)
}

export async function createFinanceTransferRequest(
  input: CreateFinanceTransferRequestInput,
): Promise<FinanceTransferRequestRecord> {
  const response = await http.post<unknown>("/finance/transfers/requests", input)
  return normalizeTransferRequest(response.data)
}

export async function getFinanceTransferRequest(id: string): Promise<FinanceTransferRequestRecord> {
  const response = await http.get<unknown>(`/finance/transfers/requests/${id}`)
  return normalizeTransferRequest(response.data)
}

/** Edit a DRAFT. Any other status is refused by the backend — a submitted request is immutable. */
export async function updateFinanceTransferRequest(input: {
  id: string
  sourceAccountId: string
  sourceFundId: string
  destinationBranchId: string
  amount: string
  occurredAt: string
  reference?: string | null
  description?: string | null
  submit?: boolean
  /** Null CLEARS a previously recorded calculation — an edit that abandons it must not keep claiming it. */
  derivation?: TransferDerivationInput | null
}): Promise<FinanceTransferRequestRecord> {
  const { id, ...body } = input
  const response = await http.patch<unknown>(`/finance/transfers/requests/${id}`, body)
  return normalizeTransferRequest(response.data)
}

export async function submitFinanceTransferRequest(id: string): Promise<FinanceTransferRequestRecord> {
  const response = await http.post<unknown>(`/finance/transfers/requests/${id}/submit`, {})
  return normalizeTransferRequest(response.data)
}

export async function approveFinanceTransferRequest(
  input: ApproveFinanceTransferRequestInput,
): Promise<FinanceTransferRequestRecord> {
  const { id, ...body } = input
  const response = await http.post<unknown>(`/finance/transfers/requests/${id}/approve`, body)
  return normalizeTransferRequest(response.data)
}

export async function rejectFinanceTransferRequest(input: {
  id: string
  rejectionReason?: string | null
}): Promise<FinanceTransferRequestRecord> {
  const response = await http.post<unknown>(`/finance/transfers/requests/${input.id}/reject`, {
    rejectionReason: input.rejectionReason ?? null,
  })
  return normalizeTransferRequest(response.data)
}

export async function cancelFinanceTransferRequest(input: {
  id: string
  cancellationReason?: string | null
}): Promise<FinanceTransferRequestRecord> {
  const response = await http.post<unknown>(`/finance/transfers/requests/${input.id}/cancel`, {
    cancellationReason: input.cancellationReason ?? null,
  })
  return normalizeTransferRequest(response.data)
}

export async function reverseFinanceTransferRequest(input: {
  id: string
  reversalReason?: string | null
}): Promise<FinanceTransferRequestRecord> {
  const response = await http.post<unknown>(`/finance/transfers/requests/${input.id}/reverse`, {
    reversalReason: input.reversalReason ?? null,
  })
  return normalizeTransferRequest(response.data)
}

/**
 * Ask the ledger how much should be transferred, for a recurring remittance rule.
 *
 * READ ONLY despite the POST — it takes a criteria object, creates nothing, and changes no balance. The
 * caller resolves the preset into concrete dates (only the client knows the branch timezone) and sends
 * the preset along as a label for the duplicate warning.
 */
export async function deriveTransferAmount(
  query: DeriveTransferAmountQuery,
): Promise<TransferDerivationResult> {
  const response = await http.post<unknown>("/finance/transfers/requests/derive-amount", query)
  return normalizeDerivationResult(response.data)
}

function normalizeDerivationResult(value: unknown): TransferDerivationResult {
  const record = (value ?? {}) as Record<string, unknown>
  const duplicate = (record.duplicate ?? null) as Record<string, unknown> | null
  return {
    mode:
      normalizeString(record.mode) === TransferDerivationMode.Fund
        ? TransferDerivationMode.Fund
        : TransferDerivationMode.IncomeAccount,
    sourceId: normalizeString(record.sourceId) ?? "",
    sourceName: normalizeNullableString(record.sourceName),
    dateFrom: normalizeString(record.dateFrom) ?? "",
    dateTo: normalizeString(record.dateTo) ?? "",
    preset: normalizeNullableString(record.preset),
    percentage: normalizeNumber(record.percentage) ?? 0,
    currency: normalizeString(record.currency) ?? "",
    // Money stays a canonical decimal string the whole way (IBA-116); it is never parsed to a float here.
    baseAmount: normalizeMoney(record.baseAmount) ?? ZERO_MONEY,
    derivedAmount: normalizeMoney(record.derivedAmount) ?? ZERO_MONEY,
    entryCount: normalizeNumber(record.entryCount) ?? 0,
    duplicate: duplicate
      ? {
          transferId: normalizeString(duplicate.transferId) ?? "",
          reference: normalizeNullableString(duplicate.reference),
          status: normalizeString(duplicate.status) ?? "",
          amount: normalizeMoney(duplicate.amount) ?? ZERO_MONEY,
          percentage: normalizeNumber(duplicate.percentage) ?? null,
          createdAt: normalizeString(duplicate.createdAt) ?? "",
        }
      : null,
  }
}

/* --- Saved calculations: named, reusable remittance rules --- */

export async function listSavedTransferCalculations(query: {
  workspaceId: string
  branchId: string
}): Promise<SavedTransferCalculation[]> {
  const response = await http.get<unknown>("/finance/transfers/requests/saved-calculations", { params: query })
  const record = (response.data ?? {}) as Record<string, unknown>
  return Array.isArray(record.items) ? record.items.map(normalizeSavedCalculation) : []
}

export async function createSavedTransferCalculation(
  input: CreateSavedTransferCalculationInput,
): Promise<{ id: string }> {
  const response = await http.post<unknown>("/finance/transfers/requests/saved-calculations", input)
  const record = (response.data ?? {}) as Record<string, unknown>
  return { id: normalizeString(record.id) ?? "" }
}

export async function deleteSavedTransferCalculation(input: {
  id: string
  workspaceId: string
  branchId: string
}): Promise<void> {
  const { id, ...params } = input
  await http.delete(`/finance/transfers/requests/saved-calculations/${encodeURIComponent(id)}`, { params })
}

function normalizeSavedCalculation(value: unknown): SavedTransferCalculation {
  const record = (value ?? {}) as Record<string, unknown>
  return {
    id: normalizeString(record.id) ?? "",
    name: normalizeString(record.name) ?? "",
    mode:
      normalizeString(record.mode) === TransferDerivationMode.Fund
        ? TransferDerivationMode.Fund
        : TransferDerivationMode.IncomeAccount,
    sourceId: normalizeString(record.sourceId) ?? "",
    preset: normalizeString(record.preset) ?? "",
    percentage: normalizeNumber(record.percentage) ?? 0,
    destinationBranchId: normalizeString(record.destinationBranchId) ?? "",
    destinationBranchName: normalizeString(record.destinationBranchName) ?? "",
    destinationBranchType: normalizeString(record.destinationBranchType) ?? "",
    sourceAccountId: normalizeString(record.sourceAccountId) ?? "",
    sourceAccountName: normalizeString(record.sourceAccountName) ?? "",
    sourceFundId: normalizeString(record.sourceFundId) ?? "",
    sourceFundName: normalizeString(record.sourceFundName) ?? "",
    createdAt: normalizeString(record.createdAt) ?? "",
  }
}
